import React, { useMemo, useState } from 'react';
import { isUndefined, isFunction } from 'lodash';
import {
  DatePicker as MuiDatePicker,
  TimePicker as MuiTimePicker,
  MobileDatePicker as MuiMobileDatePicker,
  MobileDateTimePicker as MuiMobileDateTimePicker,
} from '@mui/x-date-pickers';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';

import TextField from '../TextField/TextField';
import { datePickerFormat } from '../../services/ValidationRules';
import { CalendarIcon } from '../Form/RenderDatePickerFilter';
import { doesCountryUse12HoursFormat } from '../../constants/location';

const defaultSlotProps = ({ hideClearBtn = false, anchorEl = null }) => {
  const slotPropObj = {
    inputAdornment: {
      style: {
        width: 42,
      },
    },
    actionBar: ({ wrapperVariant }) => ({
      actions: wrapperVariant === 'desktop' || hideClearBtn ? ['accept', 'cancel'] : ['accept', 'clear', 'cancel'],
    }),
    toolbar: {
      hidden: false,
    },
    popper: {
      placement: 'bottom',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            rootBoundary: 'viewport',
            tether: false,
            altAxis: true,
          },
        },
      ],
    },
  };

  if (anchorEl !== null) {
    slotPropObj.popper.anchorEl = anchorEl;
  }

  return slotPropObj;
};

export function DatePicker(props) {
  const { format = datePickerFormat, anchorEl = null, isMobile = false, hideClearBtn = false } = props;

  const slotProps = useMemo(() => defaultSlotProps({ hideClearBtn, anchorEl }), [anchorEl]);

  const [opened, setOpened] = useState(false);

  const DatePickerComponent = isMobile ? MuiMobileDatePicker : MuiDatePicker;

  return (
    <DatePickerComponent
      {...props}
      open={isUndefined(props.open) ? opened : props.open}
      onOpen={(...openProps) => {
        setOpened(true);
        if (isFunction(props.onOpen)) {
          props.onOpen(...openProps);
        }
      }}
      onClose={(...openProps) => {
        setOpened(false);
        if (isFunction(props.onClose)) {
          props.onClose(...openProps);
        }
      }}
      closeOnSelect={false}
      format={format}
      slotProps={slotProps}
      slots={{
        openPickerIcon: () => <CalendarIcon width={20} height={20} />,
        textField: TextField,
      }}
    />
  );
}

export const DateTimePicker = (props) => {
  const { anchorEl = null, format } = props;

  const slotProps = useMemo(() => defaultSlotProps({ anchorEl }), [anchorEl]);

  const [opened, setOpened] = useState(false);

  const is12HoursFormat = doesCountryUse12HoursFormat();
  const timeFormat = is12HoursFormat ? 'hh:mm A' : 'HH:mm';
  const defaultFormat = `MMMM D, YYYY ${timeFormat}`;

  return (
    <MuiMobileDateTimePicker
      {...props}
      open={isUndefined(props.open) ? opened : props.open}
      onOpen={(...openProps) => {
        setOpened(true);
        if (isFunction(props.onOpen)) {
          props.onOpen(...openProps);
        }
      }}
      onClose={(...openProps) => {
        setOpened(false);
        if (isFunction(props.onClose)) {
          props.onClose(...openProps);
        }
      }}
      closeOnSelect={false}
      ampm={is12HoursFormat}
      format={format ?? defaultFormat}
      slotProps={slotProps}
      slots={{
        openPickerIcon: () => <CalendarIcon width={20} height={20} />,
        textField: TextField,
      }}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
    />
  );
};

export function TimePicker(props) {
  const { anchorEl = null } = props;

  const slotProps = useMemo(() => defaultSlotProps({ anchorEl }), [anchorEl]);

  const is12HoursFormat = doesCountryUse12HoursFormat();

  const [opened, setOpened] = useState(false);

  return (
    <MuiTimePicker
      {...props}
      open={isUndefined(props.open) ? opened : props.open}
      onOpen={(...openProps) => {
        setOpened(true);
        if (isFunction(props.onOpen)) {
          props.onOpen(...openProps);
        }
      }}
      onClose={(...openProps) => {
        setOpened(false);
        if (isFunction(props.onClose)) {
          props.onClose(...openProps);
        }
      }}
      closeOnSelect={false}
      ampm={is12HoursFormat}
      ampmInClock={false}
      slotProps={{
        ...(props.slotProps || {}),
        ...slotProps,
      }}
      slots={{
        openPickerIcon: () => <CalendarIcon width={20} height={20} />,
        textField: TextField,
      }}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
    />
  );
}
